export const meta = {
  id: 'sounds',
  order: 1,
  titleFi: 'Äänteet ja paino',
  titleEn: 'Sounds and stress',
  topics: [
    { id: 'letters', titleFi: 'Äänteet ja kirjaimet', titleEn: 'Sounds and letters' },
    { id: 'stress', titleFi: 'Paino', titleEn: 'Stress' },
  ],
  sources: [
    'https://wiki.languageinvention.com/index.php?title=Appendix:High_Valyrian_pronunciation',
    'https://wiki.languageinvention.com/index.php?title=Appendix:High_Valyrian_Duolingo_Tips_and_Notes#Stress',
    // Peterson: length (kēli ~ kelī), double consonants, io as one vowel (skorion), loan stress (buzdari), kh
    'https://dedalvs.tumblr.com/post/155409971972/when-romanizing-valyrian-is-it-appropriate-to-use',
    'https://dedalvs.tumblr.com/post/108188435345/what-is-high-valyrians-syllable-structure-and',
    'https://dothraki.com/2013/05/hepnon/#comment-1536',
    'https://dothraki.com/2013/04/sesir-urnebion-z%C8%B3hon-keliton-issa/',
    'https://dothraki.com/2014/01/the-treasure-of-the-wastes/#comment-7464',
    // Peterson: y = [y], often unrounded by modern speakers (grammar claim pt-g0068)
    'https://dothraki.com/2013/04/tikuni-zobri-udra-zobriar/',
  ],
}

<Topic id="letters">

<Fi>

Korkeavalyria kirjoitetaan latinalaisilla kirjaimilla, ja useimmat ääntyvät niin kuin suomalainen odottaa. <Term id="vokaali">Vokaaleja</Term> on kuusi, *a e i o u y*, ja jokainen voi olla lyhyt tai pitkä. <Term id="pitka-vokaali">Pitkä vokaali</Term> merkitään <Term id="pituusviiva">pituusviivalla</Term>: <Hv part>ā ē ī ō ū ȳ</Hv>. Se on sama vokaali pitkänä, täsmälleen kuin suomen *aa* tai *yy*, ja pituus erottaa sanoja ja muotoja kuten suomen *tuli* ja *tuuli*: <Hv>kēli</Hv> ja <Form lemma="keligon" k="present.2.sg" /> ovat eri sanoja (Petersonin esimerkki), <Hv>vala</Hv> ja <Hv>valā</Hv> saman sanan muotoja. <Hv>Y</Hv> on suomen *y*, eikä <Hv>e</Hv> ole koskaan äänetön.

*k, p, t* äännetään ilman henkäystä ja *s* on aina <Term id="soinniton">soinniton</Term>, kuten suomessa. <Define id="soinniton" compare={false} /> *r* on <Term id="taryaanne">täryäänne</Term>. *n* ääntyy *k*:n ja *g*:n edellä [ŋ], kuten sanassa *kenkä*.

<SoundGuide id="r" />

**Kaksoiskonsonantit.** <Define id="kaksoiskonsonantti" /> Näin kertoo Peterson, ja sanakirjan <Term id="ipa">IPA</Term> kirjoittaa saman: <Hv glossed>issa</Hv> ’on’, [ˈissa].

</Fi>

<En>

High Valyrian is written in the Latin alphabet, and most letters sound as a Finnish speaker expects. There are six <Term id="vokaali">vowels</Term>, *a e i o u y*, each short or long. A <Term id="pitka-vokaali">long vowel</Term> has a <Term id="pituusviiva">macron</Term>: <Hv part>ā ē ī ō ū ȳ</Hv>. It is the same vowel held longer, exactly like Finnish *aa* or *yy*, and length tells words and forms apart, as in Finnish *tuli* ‘fire’ and *tuuli* ‘wind’: <Hv>kēli</Hv> and <Form lemma="keligon" k="present.2.sg" /> are different words (Peterson’s example), <Hv>vala</Hv> and <Hv>valā</Hv> two forms of one. <Hv>Y</Hv> is the Finnish *y*, and <Hv>e</Hv> is never silent.

*k, p, t* are unaspirated and *s* is always <Term id="soinniton">voiceless</Term>, as in Finnish. <Define id="soinniton" compare={false} /> *r* is <Term id="taryaanne">trilled</Term>. *n* becomes [ŋ] before *k* and *g*, as in Finnish *kenkä*.

<SoundGuide id="r" />

**Double consonants.** <Define id="kaksoiskonsonantti" /> So says Peterson, and the dictionary’s <Term id="ipa">IPA</Term> writes the same: <Hv glossed>issa</Hv> ‘is’, [ˈissa].

</En>

<H fi="Uudet äänteet" en="New sounds" />

<div className="lesson-table-wrap" tabIndex={0}>
<table className="lesson-table">
  <thead><tr><th><T fi="Kirjain" en="Letter" /></th><th><Term id="ipa">IPA</Term></th><th><T fi="Miten" en="How" /></th></tr></thead>
  <tbody>
    <tr><td><Hv>q</Hv></td><td>[q]</td><td><T fi="k, joka äännetään paljon taempana: kielen takaosa kitakielekettä vasten" en="a k made far back: the back of the tongue against the uvula" /><SoundGuide id="q" /></td></tr>
    <tr><td><Hv>gh</Hv></td><td>[ɣ]&nbsp;~&nbsp;[ʁ]</td><td><T fi="soinnillinen kurkkuäänne: kuin g, mutta kieli ei sulje ilman kulkua, vaan ilma hankaa kielen takaosan ja suulaen välissä (espanjan g sanassa fuego)" en="a voiced throat sound: like g, but the tongue does not close off the air, which rubs between the back of the tongue and the palate (Spanish g in fuego)" /><SoundGuide id="gh" /></td></tr>
    <tr><td><Hv>rh</Hv></td><td>[r̥]</td><td><T fi="soinniton r" en="a voiceless r" /><SoundGuide id="rh" /></td></tr>
    <tr><td><Hv>ñ</Hv></td><td>[ɲ]</td><td><T fi="n ja j yhtä aikaa: kielen keskiosa kovaa suulakea vasten (espanjan ñ)" en="n and y at once: the middle of the tongue against the hard palate (Spanish ñ)" /><SoundGuide id="ny" /></td></tr>
    <tr><td><Hv>lj</Hv></td><td>[ʎ]</td><td><T fi="l ja j yhtä aikaa: kielen keskiosa kovaa suulakea vasten (lli englannin sanassa million)" en="l and y at once: the middle of the tongue against the hard palate (lli in million)" /><SoundGuide id="lj" /></td></tr>
    <tr><td><Hv>z</Hv></td><td>[z]</td><td><T fi="soinnillinen s: sano s ja anna äänihuulten väristä (englannin zoo)" en="a voiced s: say s and let the vocal cords buzz (zoo)" /><SoundGuide id="z" /></td></tr>
    <tr><td><Hv>j</Hv></td><td>[dʒ]&nbsp;~&nbsp;[ɟ]</td><td><T fi="ei suomen j: nykyään d ja ž yhteen, kuin englannin jam; vanhempi ääntämys [ɟ], jonka näet sanakirjan IPA-merkinnöissä, on d kielen keskiosa kovaa suulakea vasten" en="not a Finnish j: today d and zh together, as in jam; the older [ɟ] in the dictionary’s IPA is a d made with the middle of the tongue against the hard palate" /><SoundGuide id="j" /></td></tr>
    <tr><td><Hv>v</Hv></td><td>[w]&nbsp;~&nbsp;[v]</td><td><T fi="vanhemmassa kielessä [w] kuin englannin w, huulet pyöreinä (suomen v ei ole koskaan tällainen); nykyään [v]" en="[w] in the older language, as in English w, with rounded lips (Finnish v never is); [v] today" /><SoundGuide id="v" /></td></tr>
    <tr><td><Hv>y</Hv></td><td>[y]</td><td><T fi="suomen y: kieli kuin i:ssä, huulet pyöreinä kuin u:ssa; Petersonin mukaan nykyiset puhujat jättävät pyöristyksen usein pois, mutta sano itse y" en="the Finnish y: tongue as for i, lips rounded as for u; today’s speakers often leave out the rounding, says Peterson, but say y yourself" /><SoundGuide id="y" /></td></tr>
    <tr><td><Hv>h</Hv></td><td>[h]</td><td><Fi>kuin suomen h; älä jätä sitä pois: <Hv>hepagon</Hv> ja <Hv>epagon</Hv> ovat eri sanoja</Fi><En>as in Finnish h; do not drop it: <Hv>hepagon</Hv> and <Hv>epagon</Hv> are different words</En></td></tr>
  </tbody>
</table>
</div>

<Note>

<Fi>

**Mielikuva:** <Hv>rh</Hv> on kuin kissan kehräys, *r*:n täry ilman ääntä. Vertauksen keksi Damien Halme (Academy of High Valyrian).

</Fi>

<En>

**An image:** <Hv>rh</Hv> is like a cat’s purr, the trill of an *r* without voice. The comparison is Damien Halme’s (Academy of High Valyrian).

</En>

</Note>

<Fi>

Kirjaimet *f, kh, sh* ja *th* esiintyvät vain <Term id="lainasana">lainasanoissa</Term>. Sanakirjan IPA ääntää ne kuten alkukielessä: *th* [θ] kuin englannin *thin* (<Hv>dothraki</Hv>), *kh* [x] kuin saksan *ach* (<Hv>khalēsi</Hv>; Petersonin mukaan myös *k* tai *h*), *sh* [ʃ] kuin suomen *š* ja *f* kuin suomen *f*.

**Diftongit.** <Define id="diftongi" /> <Term id="laskeva-diftongi">Laskevat diftongit</Term> alkavat *a*:lla tai *ā*:lla ja päättyvät *e*:hen tai *o*:hon (<Hv>ae</Hv>, <Hv part>ao</Hv>, <Hv>āe</Hv>, <Hv part>āo</Hv>), ja ne ovat aina <Term id="raskas-tavu">raskaita</Term>. <Term id="nouseva-diftongi">Nousevat diftongit</Term> alkavat <Term id="liukuaanne">liukuäänteellä</Term> *i* tai *u* (<Hv>io</Hv>, <Hv>uē</Hv>); ne ovat lyhyitä tai pitkiä jälkimmäisen vokaalinsa mukaan. Tällä on väliä <Term id="paino">painolle</Term>.

**Suomalaisen ansa.** Suomessa *ae* ja *ao* eivät ole diftongeja, *ia* ja *io* jaetaan kahteen tavuun (*Ma·ri·a*), ja *ie* on <Term id="laskeva-diftongi">laskeva</Term> (*tie*). Valyriassa kaikki nämä ovat yhden tavun diftongeja, ja se ratkaisee painon: <Hv>skorion</Hv> on *SKO·rion*, ei *sko·RI·on* (Peterson).

</Fi>

<En>

The letters *f, kh, sh* and *th* occur only in <Term id="lainasana">loanwords</Term>. The dictionary’s IPA says them as in the source language: *th* [θ] as in English *thin* (<Hv>dothraki</Hv>), *kh* [x] as in German *ach* (<Hv>khalēsi</Hv>; Peterson also allows *k* or *h*), *sh* [ʃ] as in *ship* and *f* as in Finnish.

**Diphthongs.** <Define id="diftongi" /> <Term id="laskeva-diftongi">Falling diphthongs</Term> start with *a* or *ā* and end in *e* or *o* (<Hv>ae</Hv>, <Hv part>ao</Hv>, <Hv>āe</Hv>, <Hv part>āo</Hv>), and they are always <Term id="raskas-tavu">heavy</Term>. <Term id="nouseva-diftongi">Rising diphthongs</Term> start with a <Term id="liukuaanne">glide</Term> *i* or *u* (<Hv>io</Hv>, <Hv>uē</Hv>); they are short or long by their second vowel. This matters for <Term id="paino">stress</Term>.

**The Finnish trap.** In Finnish *ae* and *ao* are no diphthongs, *ia* and *io* are split into two syllables (*Ma·ri·a*), and *ie* is <Term id="laskeva-diftongi">falling</Term> (*tie*). In High Valyrian all of these are one-syllable diphthongs, and that decides the stress: <Hv>skorion</Hv> is *SKO·rion*, not *sko·RI·on* (Peterson).

</En>

<Note>

<Fi>

**Kaikki diftongit:**

- laskevat, aina raskaita: <Hv>ae</Hv>, <Hv part>āe</Hv>, <Hv part>ao</Hv>, <Hv part>āo</Hv>
- nousevat ja kevyet: <Hv part>ia</Hv>, <Hv part>ie</Hv>, <Hv>io</Hv>, <Hv part>ua</Hv>, <Hv part>ue</Hv>
- nousevat ja raskaat, kun jälkimmäinen vokaali on pitkä: <Hv part>iā</Hv>, <Hv part>iē</Hv>, <Hv part>iō</Hv>, <Hv part>uā</Hv>, <Hv>uē</Hv>

Derek Chan (High Valyrian Tutorial) lukee listan ääneen.

Viivan paikka ratkaisee: <Hv part>āe</Hv> ja <Hv part>āo</Hv> ovat diftongeja, mutta kun viiva on jälkimmäisen vokaalin päällä, *aē* ja *aō* eivät ole <Term id="diftongi">diftongeja</Term> vaan kaksi <Term id="tavu">tavua</Term>: *a·ō* (Peterson). Myös <Hv glossed>daor</Hv> ’ei’ äännetään wikin mukaan useimmiten kahtena tavuna, *da·OR*, ja niin sovelluksen ääntämisohje sen näyttää; kirjoitusasun mukainen yksitavuinen *daor* on vieressä.

</Fi>

<En>

**All the diphthongs:**

- falling, always heavy: <Hv>ae</Hv>, <Hv part>āe</Hv>, <Hv part>ao</Hv>, <Hv part>āo</Hv>
- rising and light: <Hv part>ia</Hv>, <Hv part>ie</Hv>, <Hv>io</Hv>, <Hv part>ua</Hv>, <Hv part>ue</Hv>
- rising and heavy, when the second vowel is long: <Hv part>iā</Hv>, <Hv part>iē</Hv>, <Hv part>iō</Hv>, <Hv part>uā</Hv>, <Hv>uē</Hv>

Derek Chan (High Valyrian Tutorial) reads the list aloud.

The macron’s place decides: <Hv part>āe</Hv> and <Hv part>āo</Hv> are diphthongs, but with the macron on the second vowel *aē* and *aō* are not <Term id="diftongi">diphthongs</Term> but two <Term id="tavu">syllables</Term>: *a·ō* (Peterson). <Hv glossed>Daor</Hv> ‘no’ is also most often said as two syllables, *da·OR*, says the wiki, and that is how the pronunciation guide shows it; the one-syllable *daor* of the spelling is shown beside it.

</En>

</Note>

<Note>

<Fi>

**Kurssilla kirjoitetaan latinalaisin kirjaimin.** Kirjoissa mainitaan valyrialainen kirjoitus. Peterson on pohtinut, että se voisi toimia kuin egyptiläiset hieroglyfit (jan Misali, Conlang Critic, Petersonia lainaten), ja House of the Dragon -sarjaa varten hän loi sellaisen (Peterson, The High Valyrian Writing System, 2023). Tällä kurssilla sitä ei opeteta: sanakirja ja oppitunnit käyttävät latinalaisia kirjaimia.

**Onko tämä valyriaa?** Verkossa on paljon ”valyriankielisiä” lauluja, joita Peterson ei ole kirjoittanut. Kirjoitusasu paljastaa ne usein: korkeavalyriassa ei ole kirjaimia *c*, *è*, *é* tai *ï* eikä yhdistelmää *tch*. Ainoat lisämerkit ovat pituusviiva ja <Hv part>ñ</Hv>.

**Kun kuuntelet sarjoja.** Moni näyttelijä sanoo *y*:n kuin *i*:n, ja *r* on usein yksi napautus. Peterson kertoo itse sanoneensa *q*:n *k*:na useimmissa äänitteissään. Sano itse *y*, *q* ja täryllinen *r*; näyttelijöiden ääni on kuuntelumateriaalia, ei ääntämisen malli (Derek Chan, High Valyrian Tutorial; Damien Halme, Academy of High Valyrian).

</Fi>

<En>

**This course uses the Latin alphabet.** The books mention a Valyrian script. Peterson has mused that it might work like Egyptian hieroglyphs (jan Misali, Conlang Critic, quoting Peterson), and for House of the Dragon he made one (Peterson, The High Valyrian Writing System, 2023). This course does not teach it: the dictionary and the lessons use the Latin alphabet.

**Is this Valyrian?** The web is full of “Valyrian” songs Peterson never wrote. The spelling often gives them away: High Valyrian has no *c*, *è*, *é* or *ï* and no *tch*. The only extra marks are the macron and <Hv part>ñ</Hv>.

**Listening to the shows.** Many actors say *y* like *i*, and *r* is often a single tap. Peterson says he himself said *q* as *k* in most of his recordings. Say *y*, *q* and a trilled *r* yourself; the actors’ audio is listening material, not a pronunciation model (Derek Chan, High Valyrian Tutorial; Damien Halme, Academy of High Valyrian).

</En>

</Note>

<Pronounce l="kirimvose" word="kirimvose" />

</Topic>

<Topic id="stress">

<Fi>

<Define id="paino" compare={false} /> Suomessa paino on aina ensimmäisellä <Term id="tavu">tavulla</Term>, valyriassa ei. Paino lasketaan sanan **lopusta**:

1. Paino on tavallisesti **toiseksi viimeisellä** tavulla.
2. Jos toiseksi viimeinen tavu on **kevyt** ja sitä edeltävä **raskas**, paino siirtyy kolmanneksi viimeiselle.

<Term id="kevyt-tavu">**Kevyt** tavu</Term> päättyy lyhyeen vokaaliin. <Term id="raskas-tavu">**Raskaassa** tavussa</Term> on pitkä vokaali tai <Term id="laskeva-diftongi">laskeva diftongi</Term>, tai se päättyy <Term id="konsonantti">konsonanttiin</Term>. Kaksitavuisissa sanoissa suomalainen vaisto osuu siis oikeaan, pidemmissä ei. Suomessa pitkäkään vokaali ei vedä painoa (*SIT·ruu·na*), valyriassa vetää: <Hv>zal·DRĪ·zes</Hv>.

</Fi>

<En>

<Define id="paino" compare={false} /> Finnish always stresses the first <Term id="tavu">syllable</Term>; High Valyrian does not. Stress is counted from the **end** of the word:

1. Stress normally falls on the **second-to-last** syllable.
2. If the second-to-last syllable is **light** and the one before it **heavy**, stress moves back to the third-to-last.

A <Term id="kevyt-tavu">**light** syllable</Term> ends in a short vowel. A <Term id="raskas-tavu">**heavy** one</Term> has a long vowel or a <Term id="laskeva-diftongi">falling diphthong</Term>, or ends in a <Term id="konsonantti">consonant</Term>. In two-syllable words the Finnish habit happens to be right; in longer words it is not. In Finnish even a long vowel does not pull the stress (*SIT·ruu·na*); in High Valyrian it does: <Hv>zal·DRĪ·zes</Hv>.

</En>

<div className="lesson-table-wrap" tabIndex={0}>
<table className="lesson-table">
  <thead><tr><th><T fi="Tapaus" en="Case" /></th><th><T fi="Sanat" en="Words" /></th></tr></thead>
  <tbody>
    <tr><td><T fi="kaksitavuinen: ensimmäinen tavu" en="two syllables: the first" /></td><td><Hv>kepa</Hv>, <Hv l="rytsas">rytsas</Hv>, <Hv l="vāedagon">vāedas</Hv></td></tr>
    <tr><td><T fi="toiseksi viimeinen raskas: siinä" en="second-to-last heavy: there" /></td><td><Hv>azandy</Hv>, <Hv>zaldrīzes</Hv>, <Hv>azantyssy</Hv></td></tr>
    <tr><td><T fi="toiseksi viimeinen kevyt, edellinen raskas: siirtyy taaksepäin" en="second-to-last light, the one before heavy: moves back" /></td><td><Hv>urnesi</Hv>, <Hv l="kirimvose">kirimvose</Hv>, <Hv>iotāptegon</Hv></td></tr>
    <tr><td><T fi="molemmat kevyitä: toiseksi viimeinen" en="both light: second-to-last" /></td><td><Hv>majaqis</Hv>, <Hv>sylutegon</Hv></td></tr>
  </tbody>
</table>
</div>

<Pronounce word="zaldrīzes" />

<H fi="Tavuraja" en="Where syllables split" />

<Fi>

Sanan sisällä konsonantit jakautuvat tavuihin niin, että seuraavan tavun alkuun tulee niin monta konsonanttia kuin mahdollista. Tavu voi alkaa vokaalilla, yhdellä konsonantilla tai <Term id="klusiili">klusiililla</Term> (*p, t, k, b, d, g, q*), jota seuraa <Term id="likvida">likvida</Term> (*l, r*) tai <Term id="sibilantti">sibilantti</Term> (*s, z*). *ks*, *ts* ja *ps* aloittavat aina uuden tavun (Peterson), joten niitä edeltävä tavu jää <Term id="avoin-tavu">avoimeksi</Term>: <Hv>ry·tsas</Hv>. <Term id="kaksoiskonsonantti">Kaksoiskonsonantti</Term> jakautuu kahtia: <Hv>jen·tys·sy</Hv>.

</Fi>

<En>

Inside a word, consonants split between syllables so that the next syllable starts with as many of them as it can. A syllable can start with no consonant, one consonant, or a <Term id="klusiili">stop</Term> (*p, t, k, b, d, g, q*) followed by a <Term id="likvida">liquid</Term> (*l, r*) or a <Term id="sibilantti">sibilant</Term> (*s, z*). *ks*, *ts* and *ps* always start a new syllable (Peterson), so the syllable before them stays <Term id="avoin-tavu">open</Term>: <Hv>ry·tsas</Hv>. A <Term id="kaksoiskonsonantti">double consonant</Term> splits in two: <Hv>jen·tys·sy</Hv>.

</En>

<Note>

<Fi>

**Paino neljällä kysymyksellä.** Derek Chan (High Valyrian Tutorial) käy säännön läpi kysymys kerrallaan:

1. Onko sana käskymuoto (<Term id="imperatiivi">imperatiivi</Term>)? Paino on viimeisellä tavulla: <Hv glossed>ke·lī·TĪS</Hv> ’pysähtykää!’
2. Onko toiseksi viimeinen tavu raskas? Paino on siinä: <Hv glossed>zal·DRĪ·zes</Hv> ’lohikäärme’.
3. Onko toiseksi viimeinen kevyt ja sitä edeltävä raskas? Paino on kolmanneksi viimeisellä: <Hv glossed>ki·RIM·vo·se</Hv> ’kiitos’.
4. Muuten paino on toiseksi viimeisellä: <Hv glossed>va·LO·ti</Hv> ’miesten, miehille’.

</Fi>

<En>

**Stress in four questions.** Derek Chan (High Valyrian Tutorial) walks through the rule one question at a time:

1. Is the word a command (an <Term id="imperatiivi">imperative</Term>)? The stress is on the last syllable: <Hv glossed>ke·lī·TĪS</Hv> ‘halt!’
2. Is the second-to-last syllable heavy? The stress is there: <Hv glossed>zal·DRĪ·zes</Hv> ‘dragon’.
3. Is the second-to-last light and the one before it heavy? The stress is on the third-to-last: <Hv glossed>ki·RIM·vo·se</Hv> ‘thank you’.
4. Otherwise the stress is on the second-to-last: <Hv glossed>va·LO·ti</Hv> ‘of the men, to the men’.

</En>

</Note>

<Fi>

Koska paino lasketaan lopusta, se **liikkuu**, kun sana saa tavun mittaisen päätteen. Suomessa paino pysyy alussa.

</Fi>

<En>

Because stress is counted from the end, it **moves** when the word takes an ending of a syllable or more. In Finnish it stays at the start.

</En>

<Compare fi="talo|jen" hv="val|oti" label="monikon genetiivi: TA-lo-jen, mutta va-LO-ti" />

<FormGrid lemmas="vala,ābra,zaldrīzes" cols="nom.sg,gen.pl" fi="Paino siirtyy päätteen mukana" en="The stress moves with the ending" />

<Pronounce word="valoti" />

<Fi>

**Poikkeukset.** Käskymuodoissa paino on viimeisellä tavulla (<Hv glossed>Sīmās!</Hv> ’Nouse!’). Kun ”ja” ilmaistaan pidentämällä luettelon viimeisen sanan loppuvokaali, paino siirtyy tälle tavulle: *VA·la ā·BRĀ* ’mies ja nainen’ (<TopicLink id="coordination" />). <Term id="lainasana">Lainasanoissa</Term> paino on usein samassa paikassa kuin alkukielessä: <Hv>buzdari</Hv> on *buz·DA·ri*, vaikka sääntö antaisi *BUZ·da·ri* (Peterson).

</Fi>

<En>

**Exceptions.** Imperatives are stressed on the last syllable (<Hv glossed>Sīmās!</Hv> ‘Rise!’). When ‘and’ is made by lengthening the last vowel of the last word in a list, the stress moves onto it: *VA·la ā·BRĀ* ‘a man and a woman’ (<TopicLink id="coordination" />). <Term id="lainasana">Loanwords</Term> often keep the stress of their source language: <Hv>buzdari</Hv> is *buz·DA·ri*, though the rule would give *BUZ·da·ri* (Peterson).

</En>

<Line id="hotd-s2e03-001" />

</Topic>
